import React, { useState, useEffect, useRef, useMemo } from 'react';
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  TextField,
  Button,
  Box,
  Alert,
  CircularProgress,
  List,
  ListItem,
  ListItemText,
  Paper,
  Typography,
  Popper,
  ClickAwayListener
} from '@mui/material';
import type { Category } from '../types';
import { resourceTopicService } from '../services/resourceTopicService';
import { useSnackbar } from '../hooks/useSnackbar';
import { ToastNotification } from './ToastNotification';

interface TopicFormProps {
  open: boolean;
  onClose: () => void;
  onSave: (topic: Category) => void;
  onError?: (error: string) => void;
  topic?: Category | null;
  title: string;
}

export const TopicForm: React.FC<TopicFormProps> = ({ open, onClose, onSave, onError, topic, title }) => {
  const [formData, setFormData] = useState({
    name: '',
    description: ''
  });
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [existingTopics, setExistingTopics] = useState<Category[]>([]);
  const [loadingTopics, setLoadingTopics] = useState(false);
  const [duplicateWarning, setDuplicateWarning] = useState<string | null>(null);
  const [showSuggestions, setShowSuggestions] = useState(false);
  const { snackbar, showWarning, hideSnackbar } = useSnackbar();
  const firstInputRef = useRef<HTMLInputElement>(null);
  const inputContainerRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    if (open) {
      if (topic) {
        setFormData({
          name: topic.name,
          description: topic.description || ''
        });
      } else {
        setFormData({
          name: '',
          description: ''
        });
      }
      setError(null);
      setDuplicateWarning(null);
      
      // Load existing topics for suggestions (both create and edit)
      loadExistingTopics();
      
      // Focus first input
      setTimeout(() => {
        firstInputRef.current?.focus();
      }, 100);
    }
  }, [open, topic]);

  const loadExistingTopics = async () => {
    try {
      setLoadingTopics(true);
      const response = await resourceTopicService.getTopics({ page: 1, pageSize: 1000 });
      setExistingTopics(response.categories || []);
    } catch (err) {
      console.error('Error loading existing topics:', err);
    } finally {
      setLoadingTopics(false);
    }
  };

  const normalizeText = (text: string): string => {
    return text.toLowerCase().normalize('NFD').replace(/[\u0300-\u036f]/g, '').trim();
  };

  const checkDuplicateTopic = (name: string): Category | null => {
    if (!name.trim()) return null;
    const normalizedName = normalizeText(name);
    return existingTopics.find(t => normalizeText(t.name) === normalizedName) || null;
  };

  // Filter suggestions based on input (show similar items)
  const filteredSuggestions = useMemo(() => {
    if (!formData.name.trim() || formData.name.length < 1) return [];
    const normalizedInput = normalizeText(formData.name);
    return existingTopics.filter(t => 
      normalizeText(t.name).includes(normalizedInput) || 
      t.name.toLowerCase().includes(formData.name.toLowerCase())
    ).slice(0, 10); // Limit to 10 suggestions
  }, [formData.name, existingTopics]);

  const handleInputChange = (field: 'name' | 'description') => (
    event: React.ChangeEvent<HTMLInputElement>
  ) => {
    const value = event.target.value;
    setFormData(prev => ({
      ...prev,
      [field]: value
    }));
    setError(null);
    
    // Check for duplicate in real-time (only when creating new and typing name)
    if (field === 'name' && !topic && value.trim()) {
      const duplicate = checkDuplicateTopic(value);
      if (duplicate) {
        setDuplicateWarning(`Chủ đề "${duplicate.name}" đã tồn tại.`);
      } else {
        setDuplicateWarning(null);
      }
    } else if (field === 'name') {
      setDuplicateWarning(null);
    }
  };

  const handleSubmit = async () => {
    if (!formData.name.trim()) {
      setError('Tên chủ đề là bắt buộc');
      return;
    }

    // Check for duplicate (case-insensitive) when creating new
    if (!topic) {
      const duplicate = checkDuplicateTopic(formData.name.trim());
      if (duplicate) {
        showWarning(`Chủ đề "${formData.name.trim()}" đã tồn tại.`);
        return;
      }
    }

    setLoading(true);
    setError(null);
    setDuplicateWarning(null);

    try {
      let savedTopic: Category;
      
      if (topic) {
        // Update existing topic
        savedTopic = await resourceTopicService.updateTopic(topic.id, {
          name: formData.name.trim(),
          description: formData.description.trim() || undefined
        });
      } else {
        // Create new topic
        savedTopic = await resourceTopicService.createTopic({
          name: formData.name.trim(),
          description: formData.description.trim() || undefined
        });
      }
      
      onSave(savedTopic);
      onClose();
    } catch (err: any) {
      const errorMessage = err?.message || err?.error?.message || err?.error || 'Có lỗi xảy ra khi lưu chủ đề';
      // Check if it's a duplicate error
      if (errorMessage.includes('đã tồn tại') || errorMessage.includes('already exists')) {
        showWarning(`Chủ đề "${formData.name.trim()}" đã tồn tại.`);
      } else {
        setError(errorMessage);
        if (onError) {
          onError(errorMessage);
        }
      }
    } finally {
      setLoading(false);
    }
  };

  return (
    <Dialog 
      open={open} 
      onClose={loading ? undefined : onClose}
      maxWidth="sm"
      fullWidth
    >
      <DialogTitle>{title}</DialogTitle>
      <DialogContent>
        {error && (
          <Alert severity="error" sx={{ mb: 2 }}>
            {error}
          </Alert>
        )}

        <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2, mt: 1 }}>
          <ClickAwayListener onClickAway={() => setShowSuggestions(false)}>
            <Box ref={inputContainerRef} sx={{ position: 'relative' }}>
              <TextField
                inputRef={firstInputRef}
                fullWidth
                label="Tên chủ đề *"
                value={formData.name}
                onChange={handleInputChange('name')}
                onFocus={() => setShowSuggestions(true)}
                required
                disabled={loading}
                error={!!error && !formData.name.trim() || !!duplicateWarning}
                helperText={
                  error && !formData.name.trim() 
                    ? error 
                    : duplicateWarning
                      ? duplicateWarning
                      : !topic
                        ? 'Nhập tên chủ đề. Hệ thống sẽ gợi ý các chủ đề đã có để tránh trùng lặp.'
                        : 'Nhập tên chủ đề'
                }
              />
              {/* Suggestions dropdown */}
              <Popper
                open={showSuggestions && filteredSuggestions.length > 0 && !loading}
                anchorEl={inputContainerRef.current}
                placement="bottom-start"
                style={{ zIndex: 1301, width: inputContainerRef.current?.offsetWidth }}
              >
                <Paper elevation={3} sx={{ maxHeight: 200, overflow: 'auto', mt: 0.5 }}>
                  <Typography variant="caption" sx={{ px: 2, py: 1, display: 'block', bgcolor: 'grey.100', fontWeight: 600 }}>
                    Chủ đề đã tồn tại (tham khảo):
                  </Typography>
                  <List dense sx={{ py: 0 }}>
                    {filteredSuggestions.map((item) => (
                      <ListItem 
                        key={item.id}
                        sx={{ 
                          bgcolor: normalizeText(item.name) === normalizeText(formData.name) ? 'warning.light' : 'transparent',
                          '&:hover': { bgcolor: 'grey.100' }
                        }}
                      >
                        <ListItemText 
                          primary={item.name}
                          secondary={normalizeText(item.name) === normalizeText(formData.name) ? '⚠️ Trùng tên!' : item.description}
                          primaryTypographyProps={{ 
                            fontWeight: normalizeText(item.name) === normalizeText(formData.name) ? 600 : 400 
                          }}
                        />
                      </ListItem>
                    ))}
                  </List>
                </Paper>
              </Popper>
            </Box>
          </ClickAwayListener>
          <TextField
            fullWidth
            label="Mô tả"
            value={formData.description}
            onChange={handleInputChange('description')}
            multiline
            rows={4}
            disabled={loading}
            placeholder="Nhập mô tả cho chủ đề (tùy chọn)"
          />
        </Box>
      </DialogContent>
      <DialogActions>
        <Button onClick={onClose} disabled={loading}>
          Hủy
        </Button>
        <Button
          onClick={handleSubmit}
          variant="contained"
          color="primary"
          disabled={loading || !formData.name.trim()}
          startIcon={loading ? <CircularProgress size={20} /> : null}
        >
          {loading ? 'Đang lưu...' : 'Lưu'}
        </Button>
      </DialogActions>
      <ToastNotification
        open={snackbar.open}
        message={snackbar.message}
        severity={snackbar.severity}
        onClose={hideSnackbar}
      />
    </Dialog>
  );
};

